import React, { useEffect, useState, useMemo } from 'react';
import { Award, Users, Image as ImageIcon } from 'lucide-react';
import PhotoGalleryView from '../PhotoGalleryView';
import { useTranslation } from '../../i18nContext';
import { expresidentesClosingText } from '../../data/expresidentes';
import { api } from '../../lib/api';

// Embeddable "Expresidentes" section — closing text + (optional) photo gallery +
// Malintzin award recipients + presidents timeline (all dynamic from the API).
// Rendered on the public page and as the `past_presidents` CMS block.
export default function PastPresidentsSection() {
  const t = useTranslation();
  const locale = t.locale;
  const [items, setItems] = useState([]);
  const [photoGallery, setPhotoGallery] = useState(null);

  useEffect(() => {
    api.get('/past-presidents').then(({ data }) => setItems(data || [])).catch(() => setItems([]));
    api.get('/photo-galleries/past-presidents-photos').then(({ data }) => setPhotoGallery(data)).catch(() => setPhotoGallery(null));
  }, []);

  const malintzin = useMemo(() => items.filter((x) => x.is_malintzin), [items]);
  const presidents = useMemo(() => items.filter((x) => !x.is_malintzin), [items]);
  const ppDisplayMode = photoGallery?.display_mode || 'own_page';
  const hasPhotos = !!photoGallery && photoGallery.enabled
    && (photoGallery.items || []).length > 0
    && (ppDisplayMode === 'embedded' || ppDisplayMode === 'both');

  return (
    <section className="py-16 sm:py-20 bg-white" data-testid="expresidentes-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-cmic-50 rounded-2xl p-8 sm:p-10 mb-16 border border-cmic-100">
          <p className="text-charcoal-700 text-lg leading-relaxed">{expresidentesClosingText[locale]}</p>
        </div>

        {hasPhotos && (
          <div className="mb-20" data-testid="past-presidents-photos">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-10 h-10 rounded-lg bg-cmic-500 flex items-center justify-center">
                <ImageIcon size={20} className="text-white" />
              </div>
              <h2 className="text-xl sm:text-2xl font-bold text-charcoal-800">
                {locale === 'en' ? (photoGallery.title_en || 'Photos') : (photoGallery.title_es || 'Fotografías')}
              </h2>
            </div>
            <PhotoGalleryView
              items={photoGallery.items || []}
              viewMode={photoGallery.view_mode || 'grid'}
              locale={locale}
              testidPrefix="past-presidents-photos"
              shareable
            />
          </div>
        )}

        {malintzin.length > 0 && (
          <div className="mb-20">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-10 h-10 rounded-lg bg-cmic-600 flex items-center justify-center">
                <Award size={20} className="text-white" />
              </div>
              <h2 className="text-xl sm:text-2xl font-bold text-charcoal-800">
                {locale === 'es' ? 'Recipiendaria(o)s del Premio Malintzin\u00AE' : 'Malintzin Award\u00AE Recipients'}
              </h2>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              {malintzin.map((r, i) => (
                <div key={r.id || i} className="flex items-center justify-between bg-charcoal-50 rounded-xl px-5 py-3.5 border border-charcoal-100 hover:border-cmic-200 hover:shadow-sm transition-all duration-200">
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="w-2 h-2 rounded-full bg-cmic-400 flex-shrink-0" />
                    <span className="text-charcoal-700 font-medium text-sm truncate">
                      {r.name}
                      {r.note && (<span className="text-charcoal-400 font-normal ml-1.5 text-xs italic">({r.note})</span>)}
                    </span>
                  </div>
                  <span className="text-cmic-600 font-semibold text-sm tabular-nums flex-shrink-0 ml-3">{r.award_year}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {presidents.length > 0 && (
          <div>
            <div className="flex items-center gap-3 mb-8">
              <div className="w-10 h-10 rounded-lg bg-charcoal-700 flex items-center justify-center">
                <Users size={20} className="text-white" />
              </div>
              <h2 className="text-xl sm:text-2xl font-bold text-charcoal-800">
                {locale === 'es' ? 'Presidentes del Consejo Directivo del CMIC' : 'Presidents of the CMIC Board of Directors'}
              </h2>
            </div>
            <div className="relative">
              <div className="absolute left-[19px] top-4 bottom-4 w-px bg-cmic-200" />
              <div className="space-y-1">
                {presidents.map((p, i) => {
                  const isCurrent = i === presidents.length - 1;
                  return (
                    <div key={p.id || i} className={`relative flex items-center gap-4 pl-10 pr-5 py-3 rounded-xl transition-all duration-200 ${isCurrent ? 'bg-cmic-50 border border-cmic-200' : 'hover:bg-charcoal-50 hover:shadow-sm'}`}>
                      <div className={`absolute left-[15px] w-[9px] h-[9px] rounded-full border-2 ${isCurrent ? 'bg-cmic-500 border-cmic-500 ring-4 ring-cmic-100' : 'bg-white border-cmic-300'}`} />
                      <span className={`font-medium text-sm flex-1 ${isCurrent ? 'text-cmic-700' : 'text-charcoal-700'}`}>
                        {p.name}
                        {p.note && (<span className="text-charcoal-400 font-normal ml-1.5 text-xs italic">({p.note})</span>)}
                      </span>
                      <span className={`text-sm tabular-nums flex-shrink-0 ${isCurrent ? 'text-cmic-600 font-semibold' : 'text-charcoal-400'}`}>{p.period}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
